// Shared unit-card markup used by NewPropertyView (wizard) and PropertyView
// (detail page) so the v5 .party / .unit-card pattern is identical in both
// places — same Basics fields, same Available/Edit/Close head, same nested
// edit body. The caller is responsible for wiring the data-action handlers
// (expand-unit / collapse-unit / save-unit-edit / cancel-unit-edit /
// remove-unit / submit-unit-form / cancel-unit-form) and for mounting any
// per-unit panels (meters / documents / external-owners) into the slot divs
// rendered inside the expanded body.

import { esc } from '../core/component.js';
import { icon, unitIcon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { formatMoney, formatSize } from '../core/format.js';
import { furnishedRadioHtml, energySectionHtml, amenitiesSectionHtml, countryFieldHtml, addressPasteFieldHtml } from './PropertyBasicsFields.js';
import { decodeAmenities } from '../core/propertyCatalogs.js';

const UNIT_TYPES = ['apartment', 'house', 'parking', 'garage', 'storage', 'commercial', 'other'];

// Unit types that behave like a home: rooms, max-occupants, furnishing and the
// full rental flow. Covers the land-registry "lakás" (társasházi albetét) and
// "lakóház" (kivett lakóház / családi ház). Non-residential types
// (parking/garage/storage/commercial/other) hide those fields.
const RESIDENTIAL_TYPES = ['apartment', 'house'];
export function isResidentialType(type) {
  return RESIDENTIAL_TYPES.includes(String(type || '').toLowerCase());
}

// Currency options shown in the per-unit override picker. Curated for the
// HU landlord context (forint primary, EUR for commercial leases, plus the
// usual reserve currencies). The blank value means "inherit from the
// parent property" — the column stays NULL and tenancy creation snapshots
// the resolved code.
const UNIT_CURRENCY_OPTIONS = ['HUF', 'EUR', 'USD', 'GBP', 'CHF'];

/**
 * Build an editable draft (working-copy) from a persisted unit row. Money
 * fields convert from minor to major units so they show as "1200" not
 * "120000" in the input.
 */
export function unitDraftFromRow(u) {
  return {
    identifier: u.identifier || '',
    helyrajzi_szam: u.helyrajzi_szam || '',
    type: u.type || 'apartment',
    size_sqm: u.size_sqm != null ? String(u.size_sqm) : '',
    room_count: u.room_count || '',
    floor: u.floor || '',
    addr_street: u.addr_street || '',
    addr_house_number: u.addr_house_number || '',
    addr_building: u.addr_building || '',
    addr_staircase: u.addr_staircase || '',
    addr_door: u.addr_door || '',
    city: u.city || '',
    postal_code: u.postal_code || '',
    country: u.country || '',
    notes: u.notes || '',
    // Per-unit currency override (migration 0086). '' = inherit from
    // the parent property at render/billing time; uppercased so option
    // matching in the form select is stable.
    currency: u.currency ? String(u.currency).toUpperCase() : '',
    // Per-unit security-deposit multiplier (migration 0087). '' = inherit
    // the property's default_deposit_months at wizard pre-fill time.
    default_deposit_months: u.default_deposit_months != null && u.default_deposit_months !== ''
      ? String(u.default_deposit_months) : '',
    asking_rent: u.asking_rent != null && u.asking_rent !== ''
      ? String(Math.round(Number(u.asking_rent) / 100)) : '',
    kozos_koltseg: u.kozos_koltseg != null && u.kozos_koltseg !== ''
      ? String(Math.round(Number(u.kozos_koltseg) / 100)) : '',
    furnished: u.furnished || 'unfurnished',
    max_occupants: u.max_occupants != null ? String(u.max_occupants) : '',
    energy_rating: u.energy_rating || '',
    energy_certificate_number: u.energy_certificate_number || '',
    energy_certificate_expires_at: u.energy_certificate_expires_at
      ? String(u.energy_certificate_expires_at).slice(0, 10) : '',
    // Amenities (felszereltség) — JSON-string in the column, array in the draft.
    amenities: decodeAmenities(u.amenities),
  };
}

/**
 * Empty draft suitable for the "+ Add another unit" form.
 */
export function emptyUnitDraft() {
  return {
    identifier: '', helyrajzi_szam: '', type: 'apartment',
    size_sqm: '', room_count: '', floor: '',
    addr_street: '', addr_house_number: '', addr_building: '', addr_staircase: '', addr_door: '',
    city: '', postal_code: '', country: '',
    notes: '',
    asking_rent: '', kozos_koltseg: '', currency: '', default_deposit_months: '',
    furnished: 'unfurnished', max_occupants: '',
    energy_rating: '', energy_certificate_number: '', energy_certificate_expires_at: '',
    amenities: [],
  };
}

/**
 * Basics field rows shared by the unit edit form, the "+ add unit" form and
 * the unit-creation wizard: identifier + type, the money row (asking rent +
 * közös költség — kept together so they read as one monthly figure), size,
 * and the apartment-only room-count + max-occupants row. The money fields
 * live here (not in the terms block) because the short wizard flows for
 * non-apartment unit types skip the terms/details step but still need rent.
 * `data-apartment-only` rows are toggled live by wireUnitFormApartmentOnly();
 * they also render `hidden` up-front based on the draft's type so
 * non-apartment drafts don't flash.
 */
export function unitBasicsFieldsHtml(d, currency = 'HUF') {
  // Effective currency = the unit's own override when set, otherwise the
  // property's default (the `currency` arg). The label suffix on the
  // money inputs reflects this so the user sees the actual currency the
  // amount will be stored as.
  const effective = (d.currency && String(d.currency).toUpperCase()) || (currency || 'HUF').toUpperCase();
  const cur = esc(effective);
  const inheritLabel = esc(t('property.unit_form.currency_inherit', { code: (currency || 'HUF').toUpperCase() })
    || `Inherit (${(currency || 'HUF').toUpperCase()})`);
  // Build the <option>s once: an "inherit" entry mapped to '' plus the
  // curated unit-currency list. The unit's stored value wins as the
  // selected option even when it's outside the curated list (so an old
  // RON row stays editable instead of silently flipping to inherit).
  const codes = d.currency && !UNIT_CURRENCY_OPTIONS.includes(String(d.currency).toUpperCase())
    ? [...UNIT_CURRENCY_OPTIONS, String(d.currency).toUpperCase()]
    : UNIT_CURRENCY_OPTIONS;
  const currencyOptionsHtml = `
    <option value="" ${d.currency ? '' : 'selected'}>${inheritLabel}</option>
    ${codes.map((c) => `<option value="${c}" ${String(d.currency || '').toUpperCase() === c ? 'selected' : ''}>${c}</option>`).join('')}`;
  return `
    <div class="field-row">
      <div class="field"><label>${esc(t('property.unit_form.identifier'))}</label>
        <input class="input" name="identifier" required value="${esc(d.identifier || '')}" /></div>
      <div class="field"><label>${esc(t('property.unit_form.type'))}</label>
        <select class="select" name="type">
          ${UNIT_TYPES.map((k) => `<option value="${k}" ${d.type === k ? 'selected' : ''}>${esc(t('property.unit_form.types.' + k))}</option>`).join('')}
        </select></div>
    </div>
    <div class="field-row">
      <div class="field" style="flex:0 1 160px"><label>${esc(t('property.unit_form.currency') || 'Pénznem')}</label>
        <select class="select" name="currency" data-unit-currency>${currencyOptionsHtml}</select></div>
      <div class="field"><label>${esc(t('property.unit_form.asking_rent'))} <span class="faint" style="text-transform: none; letter-spacing: 0; font-size: 12px;" data-currency-suffix>(${cur})</span></label>
        <input class="input" name="asking_rent" type="number" step="1" min="0" value="${esc(d.asking_rent || '')}" /></div>
      <div class="field"><label>${esc(t('property.unit_form.kozos_koltseg') || 'Közös költség / mo')} <span class="field-hint">— ${esc(t('property.unit_form.kozos_koltseg_hint') || 'building fee, auto-billed')}</span> <span class="faint" style="text-transform: none; letter-spacing: 0; font-size: 12px;" data-currency-suffix>(${cur})</span></label>
        <input class="input" name="kozos_koltseg" type="number" step="1" min="0" value="${esc(d.kozos_koltseg || '')}" placeholder="0" /></div>
    </div>
    <div class="field-row">
      <div class="field"><label>${esc(t('property.unit_form.size'))}</label>
        <input class="input" name="size_sqm" type="number" step="0.01" min="0" value="${esc(d.size_sqm || '')}" /></div>
      <div class="field"><label>${esc(t('property.unit_form.default_deposit_months') || 'Kaució (hónap × bér)')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${esc(t('common.optional') || 'opcionális')})</span></label>
        <input class="input" name="default_deposit_months" type="number" step="1" min="0" max="24" inputmode="numeric" value="${esc(d.default_deposit_months || '')}" placeholder="${esc(t('property.unit_form.default_deposit_months_placeholder') || 'ingatlan alapért.')}" />
        <span class="faint" style="text-transform:none; letter-spacing:0; font-size:11.5px;">${esc(t('property.unit_form.default_deposit_months_help') || 'Üresen az ingatlan alapértelmezettjét örökli.')}</span></div>
    </div>
    <div class="field-row" data-apartment-only ${isResidentialType(d.type) ? '' : 'hidden'}>
      <div class="field"><label>${esc(t('property.unit_form.room_count') || 'Szobaszám')} <span class="faint" style="text-transform: none; letter-spacing: 0; font-size: 12px;">${esc(t('property.unit_form.room_count_hint') || 'pl. 2+1')}</span></label>
        <input class="input" name="room_count" maxlength="16" value="${esc(d.room_count || '')}" placeholder="2+1" /></div>
      <div class="field"><label>${esc(t('property.unit_form.max_occupants') || 'Max occupants')}</label>
        <input class="input" name="max_occupants" type="number" step="1" min="0" value="${esc(d.max_occupants || '')}" placeholder="—" /></div>
    </div>`;
}

/**
 * Furnishing selector — apartment-only, hidden for other unit types. Split out
 * from the money fields (now in unitBasicsFieldsHtml) so the wizard's
 * "furnishing + energy" step can render it on its own.
 */
export function unitTermsFieldsHtml(d) {
  return `
    <div class="field" data-apartment-only ${isResidentialType(d.type) ? '' : 'hidden'}>
      <label>${esc(t('property.unit_form.furnished') || 'Furnished')}</label>
      ${furnishedRadioHtml(d.furnished)}
    </div>`;
}

/**
 * Shared markup for the optional "own address" section. Rendered as a
 * <details> that auto-opens if any address override field is filled.
 */
export function unitAddressSectionHtml(d) {
  const hasOverride = !!(d.addr_street || d.addr_house_number || d.addr_building
    || d.addr_staircase || d.floor || d.addr_door || d.helyrajzi_szam
    || d.city || d.postal_code || d.country || d.notes);
  const open = hasOverride ? 'open' : '';
  const t_ = (k, fb) => esc(t(k) || fb);
  return `
    <details class="unit-address-section" ${open}>
      <summary class="unit-address-summary">
        <span class="eyebrow">${t_('property.unit_form.address_section', 'Cím + megjegyzések')}</span>
        <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">
          ${t_('property.unit_form.address_hint', '— ha üres, az ingatlan címét örökli')}
        </span>
      </summary>
      <div class="unit-address-grid" style="margin-top:12px;">
        ${addressPasteFieldHtml()}
        <div class="field-row three">
          <div class="field"><label>${t_('property.unit_form.postal_code', 'Irányítószám')}</label>
            <input class="input" name="postal_code" maxlength="32" value="${esc(d.postal_code || '')}" placeholder="1062" /></div>
          <div class="field"><label>${t_('property.unit_form.city', 'Város')}</label>
            <input class="input" name="city" maxlength="128" value="${esc(d.city || '')}" placeholder="Budapest" /></div>
          <div class="field"><label>${t_('property.unit_form.country', 'Ország')}</label>
            ${countryFieldHtml(d.country || '', { allowBlank: true })}</div>
        </div>
        <div class="field-row">
          <div class="field" style="flex:2 1 200px"><label>${t_('property.unit_form.addr_street', 'Utca, közterület')}</label>
            <input class="input" name="addr_street" maxlength="190" value="${esc(d.addr_street || '')}" placeholder="Andrássy út" /></div>
          <div class="field" style="flex:1 1 100px"><label>${t_('property.unit_form.addr_house_number', 'Házszám')}</label>
            <input class="input" name="addr_house_number" maxlength="32" value="${esc(d.addr_house_number || '')}" placeholder="12" /></div>
        </div>
        <div class="field-row">
          <div class="field"><label>${t_('property.unit_form.addr_building', 'Épület')}</label>
            <input class="input" name="addr_building" maxlength="32" value="${esc(d.addr_building || '')}" placeholder="B" /></div>
          <div class="field"><label>${t_('property.unit_form.addr_staircase', 'Lépcsőház')}</label>
            <input class="input" name="addr_staircase" maxlength="32" value="${esc(d.addr_staircase || '')}" /></div>
          <div class="field"><label>${t_('property.unit_form.floor', 'Emelet')}</label>
            <input class="input" name="floor" maxlength="16" value="${esc(d.floor || '')}" placeholder="3" /></div>
          <div class="field"><label>${t_('property.unit_form.addr_door', 'Ajtó')}</label>
            <input class="input" name="addr_door" maxlength="32" value="${esc(d.addr_door || '')}" placeholder="5" /></div>
        </div>
        <div class="field"><label>${t_('property.unit_form.helyrajzi_szam', 'Helyrajzi szám')} <span class="faint" style="text-transform:none; letter-spacing:0; font-size:12px;">(${t_('common.optional', 'opcionális')})</span></label>
          <input class="input" name="helyrajzi_szam" maxlength="64" value="${esc(d.helyrajzi_szam || '')}" placeholder="39221/27/A/118" /></div>
        <div class="field">
          <label>${t_('property.unit_form.notes', 'Megjegyzések · kapucsengő, hozzáférés')}</label>
          <textarea class="input" name="notes" rows="3" maxlength="2000" placeholder="${t_('property.unit_form.notes_placeholder', 'pl. Kapucsengő: 12 · Bejárat az udvar felől · Gondnok: +36 30 000 0000')}">${esc(d.notes || '')}</textarea>
          <span class="faint" style="font-size:11.5px;">${t_('property.unit_form.notes_hint', 'Belső jegyzet — csak a tulajdonos és az operátor látja.')}</span>
        </div>
      </div>
    </details>`;
}

/**
 * Render one unit card. `draft` (working-copy of editable fields) when
 * present puts the card in the expanded edit state; otherwise it renders
 * collapsed as a head row only.
 */
export function unitCardHtml({ unit, index, draft, currency = 'HUF', slots = ['meters'], href = null }) {
  // `href` turns the card into a navigational link (collapsed, no inline
  // edit) — used on the property page where a click opens the unit's page.
  const expanded = !!draft && !href;
  const num = String(index + 1).padStart(2, '0');
  const typeLabel = esc(t('property.unit_form.types.' + unit.type) || unit.type);
  // Occupied units name their tenant right on the badge — the "who lives
  // here" question shouldn't need a trip to the tenancies list.
  const tenantSuffix = unit.current_tenant_name ? `${esc(unit.current_tenant_name)} · ` : '';
  const availBadge = unit.current_tenancy_open_ended
    ? `<span class="badge badge-warning">${tenantSuffix}${esc(t('property.unit_row.occupied_open_ended') || 'Occupied')}</span>`
    : unit.current_tenancy_end_date
      ? `<span class="badge badge-warning">${tenantSuffix}${esc(t('property.unit_row.occupied_until', { date: unit.current_tenancy_end_date }) || 'Occupied')}</span>`
      : `<span class="badge success">${esc(t('property.wizard.units.available') || 'Available')}</span>`;
  const toggleLabel = expanded ? (t('common.close') || 'Close') : (t('common.edit') || 'Edit');

  // v5 .party-head inline summary: identifier in strong, then italicised
  // `· type · size · rent + közös` so the collapsed card communicates the
  // key economics at a glance. Suffix-less fields are skipped silently.
  const isApartment = isResidentialType(unit.type);
  const summaryBits = [typeLabel];
  if (unit.size_sqm != null && unit.size_sqm !== '') summaryBits.push(`${esc(formatSize(unit.size_sqm))} m²`);
  if (isApartment && unit.room_count) summaryBits.push(`${esc(unit.room_count)} ${esc(t('property.unit_form.room_count_short') || 'szoba')}`);
  if (unit.asking_rent != null && unit.asking_rent !== '') {
    const rent = esc(formatMoney(Number(unit.asking_rent), currency));
    const kozos = unit.kozos_koltseg ? ` + ${esc(formatMoney(Number(unit.kozos_koltseg), currency))}` : '';
    summaryBits.push(`${rent}${kozos}/${esc((t('public.per_month_suffix') || '/mo').replace(/^\//, ''))}`);
  } else if (unit.kozos_koltseg) {
    summaryBits.push(`${esc(formatMoney(Number(unit.kozos_koltseg), currency))} ${esc(t('property.unit_form.kozos_koltseg') || 'közös')}`);
  }
  const summaryHtml = `<em class="unit-card-summary">· ${summaryBits.join(' · ')}</em>`;

  // Mono meta line: furnished + helyrajzi szám. Uppercase, faint — same
  // pattern as v5 .party-meta.
  const metaBits = [];
  if (isApartment && unit.furnished) {
    const fkey = 'property.unit_form.furnished_' + String(unit.furnished).replace('partly_furnished', 'partly').replace('fully_furnished', 'fully');
    metaBits.push(String(t(fkey) || unit.furnished).toUpperCase());
  }
  if (isApartment && unit.max_occupants != null && unit.max_occupants !== '') {
    metaBits.push(`${esc(String(unit.max_occupants))} ${esc(String(t('property.unit_form.max_occupants') || 'Max').toUpperCase())}`);
  }
  if (isApartment && unit.floor) metaBits.push(`${esc(String(t('property.unit_form.floor') || 'Emelet').toUpperCase())} ${esc(unit.floor)}`);
  if (unit.helyrajzi_szam) metaBits.push(`HRSZ ${esc(unit.helyrajzi_szam)}`);
  if (unit.addr_street || unit.address_line1 || unit.city) {
    metaBits.push(esc(String(t('property.unit_form.own_address_badge') || 'Saját cím').toUpperCase()));
  }
  const metaHtml = metaBits.length ? `<div class="unit-card-meta-line mono">${metaBits.join(' · ')}</div>` : '';

  const tag = href ? 'a' : 'div';
  const openHref = href ? ` href="${esc(href)}"` : '';
  const actions = href
    ? `${availBadge}<span class="btn-mono unit-card-open">${esc(t('unit.open') || 'Megnyitás')} ${icon('chevron-right', { size: 'sm' })}</span>`
    : `${availBadge}<button type="button" class="btn-mono" data-action="${expanded ? 'collapse-unit' : 'expand-unit'}" data-id="${esc(unit.id)}">${esc(toggleLabel)}</button>`;
  return `
    <${tag} class="unit-card${expanded ? ' expanded' : ''}${href ? ' unit-card-link' : ''}"${openHref} data-unit-block="${esc(unit.id)}">
      <div class="unit-card-head">
        <span class="unit-card-num mono">${num}</span>
        <div class="unit-card-icon">${icon(unitIcon(unit.type), {})}</div>
        <div class="unit-card-info">
          <div class="unit-card-name">
            <strong>${esc(unit.identifier || '—')}</strong>
            ${summaryHtml}
          </div>
          ${metaHtml}
        </div>
        <div class="unit-card-actions">
          ${actions}
        </div>
      </div>
      ${expanded ? _editBodyHtml(unit, draft, currency, slots) : ''}
    </${tag}>`;
}

function _editBodyHtml(unit, draft, currency, slots) {
  const slotsHtml = (slots || []).map((s) => {
    if (s === 'meters')    return `<div data-unit-meters-slot="${esc(unit.id)}"></div>`;
    if (s === 'documents') return `<div data-unit-documents-slot="${esc(unit.id)}"></div>`;
    if (s === 'owners')    return `<div data-unit-external-owners-slot="${esc(unit.id)}"></div>`;
    return '';
  }).join('');
  return `
    <div class="party-edit" data-unit-edit="${esc(unit.id)}">
      <form class="unit-form" data-unit-edit-form data-unit-id="${esc(unit.id)}" autocomplete="off">
        <div class="eyebrow" style="margin-bottom: 10px;">${esc(t('property.wizard.units.basics') || 'Basics')}</div>
        <div data-form-error></div>
        ${unitBasicsFieldsHtml(draft, currency)}
        ${unitTermsFieldsHtml(draft, currency)}
        ${unitAddressSectionHtml(draft)}
        <div class="eyebrow" style="margin: 14px 0 6px;">${esc(t('property.wizard.steps.energy') || 'Energetika')}</div>
        ${energySectionHtml({
          energy_rating: draft.energy_rating,
          energy_certificate_number: draft.energy_certificate_number,
          energy_certificate_expires_at: draft.energy_certificate_expires_at,
        }, { showDropzone: false, showHelp: false })}
        ${amenitiesSectionHtml(draft.amenities)}
      </form>

      <div class="unit-card-detail">${slotsHtml}</div>

      <div class="unit-form-actions">
        <button type="button" class="btn btn-sm" data-action="save-unit-edit" data-id="${esc(unit.id)}">${esc(t('property.wizard.units.save') || 'Save unit')}</button>
        <button type="button" class="btn-mono" data-action="cancel-unit-edit" data-id="${esc(unit.id)}">${esc(t('common.cancel'))}</button>
        <button type="button" class="btn-mono danger" data-action="remove-unit" data-id="${esc(unit.id)}" style="margin-left: auto;">
          ${esc(t('property.wizard.units.remove') || 'Remove unit')}
        </button>
      </div>
    </div>`;
}

/**
 * Render the "+ Add another unit" form using the same .unit-card.expanded
 * chrome so the visual jump from list → editor is just an expanded
 * placeholder card.
 */
export function unitFormHtml({ newUnit, index = null, currency = 'HUF', busy = false, showAddAnother = true, chrome = true, saveLabel = null }) {
  const numLabel = index != null ? String(index).padStart(2, '0') : '';
  const newLabel = esc(t('property.wizard.units.form_label') || 'New unit');
  const pendingBadge = `<span class="badge badge-warning unit-card-pending-badge">${esc(t('property.wizard.units.pending_badge') || 'Mentésre vár')}</span>`;
  // `chrome` wraps the form in the .unit-card placeholder (wizard / property
  // add-form). The unit page sets chrome:false to drop the placeholder head.
  const head = chrome ? `
      <div class="unit-card-head">
        ${numLabel ? `<span class="unit-card-num mono">${numLabel}</span>` : '<span></span>'}
        <div class="unit-card-info">
          <div class="unit-card-name"><strong>${newLabel}</strong></div>
        </div>
        <div class="unit-card-actions">${pendingBadge}</div>
      </div>` : '';
  return `
    <div class="${chrome ? 'unit-card expanded unit-card-draft' : 'unit-page-form'}">
      ${head}
      <div class="party-edit">
        <form class="unit-form" data-unit-form autocomplete="off">
          <div class="eyebrow" style="margin-bottom: 10px;">${esc(t('property.wizard.units.basics') || 'Basics')}</div>
          <div data-form-error></div>
          ${unitBasicsFieldsHtml(newUnit, currency)}
          ${unitTermsFieldsHtml(newUnit, currency)}
          ${unitAddressSectionHtml(newUnit)}
          <div class="eyebrow" style="margin: 14px 0 6px;">${esc(t('property.wizard.steps.energy') || 'Energetika')}</div>
          ${energySectionHtml({
            energy_rating: newUnit.energy_rating,
            energy_certificate_number: newUnit.energy_certificate_number,
            energy_certificate_expires_at: newUnit.energy_certificate_expires_at,
          }, { showDropzone: false, showHelp: false })}
          ${amenitiesSectionHtml(newUnit.amenities)}
        </form>
        <div class="unit-form-actions">
          <button type="button" class="btn btn-primary" data-action="submit-unit-form" ${busy ? 'disabled' : ''}>${icon('check', { size: 'sm' })}<span>${esc(saveLabel || t('property.wizard.units.save_unit') || t('property.wizard.units.save') || 'Egység mentése')}</span></button>
          ${showAddAnother ? `<button type="button" class="btn btn-ghost" data-action="submit-unit-form-add-another" ${busy ? 'disabled' : ''}>${icon('plus', { size: 'sm' })}<span>${esc(t('property.wizard.units.save_and_add') || 'Mentés + új egység')}</span></button>` : ''}
          <button type="button" class="btn-mono" data-action="cancel-unit-form">${esc(t('common.cancel'))}</button>
        </div>
      </div>
    </div>
  `;
}

export { UNIT_TYPES };

/**
 * Wire the apartment-only field toggles in a unit form: when `type`
 * changes to "parking" or "storage", hide the furnished select + max
 * occupants input (those don't make sense for non-apartments). Call
 * from any caller that mounts a unit-edit or new-unit form so the
 * behavior is the same in NewPropertyView and PropertyView.
 */
export function wireUnitFormApartmentOnly(formEl) {
  if (!formEl) return;
  const typeSel = formEl.querySelector('[name="type"]');
  if (!typeSel) return;
  const apply = () => {
    const isApartment = isResidentialType(typeSel.value);
    formEl.querySelectorAll('[data-apartment-only]').forEach((el) => {
      el.hidden = !isApartment;
    });
  };
  typeSel.addEventListener('change', apply);
  apply();
}

/**
 * Live-update the "(HUF)" suffix on the rent / közös költség labels
 * whenever the per-unit currency picker changes. `inheritFrom` is the
 * parent property's currency, used when the picker is on the blank
 * (inherit) value. Idempotent — safe to call after every render.
 */
export function wireUnitCurrencySuffix(formEl, inheritFrom = 'HUF') {
  if (!formEl) return;
  const sel = formEl.querySelector('[data-unit-currency]');
  if (!sel) return;
  const fallback = (inheritFrom || 'HUF').toUpperCase();
  const apply = () => {
    const code = (sel.value || '').toUpperCase() || fallback;
    formEl.querySelectorAll('[data-currency-suffix]').forEach((el) => {
      el.textContent = `(${code})`;
    });
  };
  sel.addEventListener('change', apply);
  apply();
}

// The editable draft keys (mirrors emptyUnitDraft) — used to read a (possibly
// partial) form back into a draft without picking up unrelated inputs.
// `amenities` is a chip group (not a FormData entry) so it's handled
// separately in readUnitFormIntoDraft below.
const UNIT_DRAFT_KEYS = Object.keys(emptyUnitDraft()).filter((k) => k !== 'amenities');

/**
 * Merge the unit fields present in `form` over `base`, returning a new draft.
 * Only keys that exist in the form are overwritten, so a partial step form
 * (e.g. the wizard's basics step) leaves the draft's other fields untouched.
 * The amenities chip group is read off the DOM (not FormData) so a form that
 * renders the chips replaces base.amenities; a form that omits them leaves
 * base.amenities alone.
 */
export function readUnitFormIntoDraft(form, base) {
  const fd = new FormData(form);
  const out = { ...base };
  for (const k of UNIT_DRAFT_KEYS) {
    if (fd.has(k)) out[k] = (fd.get(k) ?? '').toString();
  }
  if (form.querySelector('[data-amenity]')) {
    out.amenities = Array.from(form.querySelectorAll('[data-amenity].chip-on'))
      .map((b) => b.getAttribute('data-amenity'))
      .filter(Boolean);
  }
  return out;
}

/**
 * Build the /units API payload from a draft object. Money fields convert
 * major→minor, blanks become null/empty. `identifier` is required — returns
 * `{ _error: 'identifier' }` when missing so callers can surface a field error.
 * Sends the full field set (the backend UPDATE is a full row replace).
 */
export function unitPayloadFromDraft(d) {
  const g = (k) => (d[k] ?? '').toString().trim();
  const identifier = g('identifier');
  if (!identifier) return { _error: 'identifier' };
  const payload = { identifier, type: g('type') || 'apartment' };
  if (g('size_sqm')) payload.size_sqm = g('size_sqm');
  payload.room_count = g('room_count');
  payload.floor = g('floor');
  const rent = parseFloat(g('asking_rent'));
  payload.asking_rent = Number.isFinite(rent) ? Math.round(rent * 100) : null;
  const kozos = parseFloat(g('kozos_koltseg'));
  payload.kozos_koltseg = Number.isFinite(kozos) ? Math.round(kozos * 100) : null;
  // Per-unit currency override: '' = inherit from property (column stays
  // NULL); otherwise a 3-letter ISO code (validated server-side).
  payload.currency = g('currency') ? g('currency').toUpperCase() : '';
  // Per-unit deposit multiplier: '' = inherit (column stays NULL); otherwise
  // a 0..24 integer (validated server-side).
  payload.default_deposit_months = g('default_deposit_months') !== ''
    ? Number(g('default_deposit_months')) : null;
  if (g('furnished')) payload.furnished = g('furnished');
  const maxOcc = parseInt(g('max_occupants'), 10);
  payload.max_occupants = Number.isFinite(maxOcc) && maxOcc >= 0 ? maxOcc : null;
  for (const k of ['addr_street', 'addr_house_number', 'addr_building', 'addr_staircase', 'addr_door', 'postal_code', 'city', 'country', 'helyrajzi_szam', 'notes']) {
    payload[k] = g(k);
  }
  payload.energy_rating = g('energy_rating');
  payload.energy_certificate_number = g('energy_certificate_number');
  payload.energy_certificate_expires_at = g('energy_certificate_expires_at');
  payload.amenities = Array.isArray(d.amenities) ? d.amenities.slice() : [];
  return payload;
}

/** Convenience: build a payload straight from a full unit form. */
export function unitPayloadFromForm(form) {
  return unitPayloadFromDraft(readUnitFormIntoDraft(form, emptyUnitDraft()));
}
